import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';


import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { AppState } from '../../reducers';
import { Observable, Subscription } from "rxjs";

import * as model from "../../models";
import {
	Address,
	AvailableShipmentTypeDTO,
	BookingOffice,
	Category,
	DefaultPaymentTypeBoxOffice,
	DefaultShipmentTypeBoxOffice,
	DisplayTicket,
	ErrorMessages,
	Event,
	OrderDTO,
	PaymentTypesBoxOffice,
	ShipmentTypesBoxOffice,
	TicketQuota,
	Venue
} from "../../models";
import * as ui from "../../models/ui"
import { CombinedPriceLevel } from "../../models/ui"
import { MerchantApi, PrinterApi, TicketCreationDTO, TicketQuotaApi } from "../../services/webservice"
import { BoxOfficeActions } from "./ngrx/box-office.actions"
import { CheckoutActions } from "../checkout-components/checkout.actions";
import { Money } from 'app/models/money.model';
import { SeatingplanServerState } from "../../reducers/seatingplan-server.reducer";
import { TicketQuotaActions } from "../../actions";

@Component({
	selector: 'box-office',
	templateUrl: "./box-office.html",
	styles: [require("./box-office.scss")],
	changeDetection: ChangeDetectionStrategy.OnPush
})

export class BoxOffice implements OnInit, OnDestroy {
	// SHOPPING CART
	shoppingCartHasErrors$: Observable<boolean>;

	// SEATING-PLAN
	seatingPlan$: Observable<SeatingplanServerState>;
	isSpBoxOffice$: Observable<boolean>;
	blockSeatElements$: Observable<{ blockSeating: any, displayCategory: any }[]>;
	visualTickets$: Observable<DisplayTicket[]>;
	displayTicketsPerTicketQuota$: Observable<{ticketQuotaName: string, amountOfTickets: number, subtotal: number}[]>;

	// QUOTA
	selectedTicketQuota$: Observable<TicketQuota>

	// SEARCH AND ORDER
	private orderBy: string;
	private search: any;
	// ERRORS
	private errorMessagesExist: boolean;
	private errorMessages: ErrorMessages;

	// SHOPPING CART
	private dscSecureId: string;
	private numberOfItemsInShoppingCart: number;
	private combinedPricelevels$: Store<CombinedPriceLevel[]>;
	private categories$: Store<Category[]>;
	private displayCategoriesWithDisplayTickets$: Observable<ui.DisplayCategoryWithDisplayTicket[]>;
	private ticketQuotas$: Observable<TicketQuota[]>;
	private displayShoppingCart$: Observable<model.DisplayShoppingCart>;
	private selectedTicketQuota: TicketQuota;
	private shoppingCart: model.ShoppingCart;

	// TICKET ADDRESS
	private amountTicketAdresses: number = 0;
	private ticketAddresses: Record<string, Address>;
	private showTicketAddressFormsPopup: boolean = false;

	// PAYMENT/SHIPMENT
	private paymentTypesEnumBoxOffice = PaymentTypesBoxOffice;
	private defaultPaymentTypeBoxOffice = DefaultPaymentTypeBoxOffice;
	private paymentTypesKeysBoxOffice = Object.keys(PaymentTypesBoxOffice);
	private shipmentTypesEnumBoxOffice = ShipmentTypesBoxOffice;
	private shipmentTypesKeysBoxOffice = Object.keys(ShipmentTypesBoxOffice);
	private defaultShipmentTypeBoxOffice = DefaultShipmentTypeBoxOffice;
	private paymentType$: Observable<string>;
	private shipmentType$: Observable<string>;
	private bookingOffice$: Observable<BookingOffice>;
	private availableShipmentTypesInfo$: Observable<AvailableShipmentTypeDTO>;
	private availablePaymentTypes$: Observable<string[]>;
	private shipmentFees: Money;
	private paymentType: string;
	private shipmentType: string;

	// TOTAL
	private shoppingCartTotal$: Observable<Number>;

	// PRINTING
	private documentsToPrint: model.DocumentsToPrint;
	private printerList$: Observable<model.Printer[]>;
	private ticketPrinterId: string;

	private subscriptions: Subscription[] = [];
	private event$: Observable<Event>;
	private venue$: Observable<Venue>;
	private showTicketQuotaDropDown: boolean = true;

	constructor(
		private store: Store<AppState>,
		private route: ActivatedRoute,
		private checkoutActions: CheckoutActions,
		private boxOfficeActions: BoxOfficeActions,
		private ticketQuotaActions: TicketQuotaActions,
		private printerApi: PrinterApi,
		private merchantApi: MerchantApi,
		private ticketQuotaApi: TicketQuotaApi
	) {
	}

	ngOnInit() {
		// search params
		this.subscriptions.push(this.store.select("eventsOnSale", "search").subscribe(searchparams =>
			this.search = {
				eventTitle: searchparams.eventTitle ? searchparams.eventTitle : '',
				venueName: searchparams.venueName ? searchparams.venueName : '',
				from: searchparams.from ? searchparams.from : '',
				to: searchparams.to ? searchparams.to : '',
			}
		));
		// order param
		this.subscriptions.push(this.store.select("eventsOnSale", "order").subscribe(orderBy =>
			this.orderBy = orderBy
		));
		this.subscriptions.push(this.store.select("shoppingCart", "shoppingCart").subscribe(shoppingCart => {
			if (!shoppingCart) {
				return;
			}
			this.store.dispatch(this.checkoutActions.loadAvailableShipmentTypes(shoppingCart.secureId));
			this.shoppingCart = shoppingCart
			this.numberOfItemsInShoppingCart = shoppingCart && shoppingCart.ticketItems ? shoppingCart.ticketItems.length : 0
			if (!!this.selectedTicketQuota) {
				this.ticketQuotaApi.getTicketQuota(this.selectedTicketQuota.storageId).subscribe(
					quota => this.store.dispatch(this.boxOfficeActions.refreshSelectedTicketQuotaBoxOffice(quota))
				)
			}
		}));
		this.shoppingCartHasErrors$ = this.store.select("shoppingCart", "errors")
		this.event$ = this.store.select("eventServerState", "event", "currentEvent");
		this.venue$ = this.store.select("venuesState", "currentVenue");

		this.ticketQuotas$ = this.store.select("eventServerState", "ticketQuotas", "ticketQuotaCollection");
		this.selectedTicketQuota$ = this.store.select("eventServerState", "ticketQuotas", "selectedTicketQuota");
		this.subscriptions.push(this.store.select("eventServerState", "ticketQuotas", "selectedTicketQuota")
			.subscribe(selectedQuota => this.selectedTicketQuota = selectedQuota))
		this.subscriptions.push(this.ticketQuotas$.subscribe(data => {
			this.showTicketQuotaDropDown = data.length > 1;
		}));
		this.displayShoppingCart$ = this.store.select("shoppingCart", "displayShoppingCart");
		this.subscriptions.push(this.displayShoppingCart$.subscribe(dsc => dsc ? this.dscSecureId = dsc.secureId : null))
		this.categories$ = this.store.select("eventServerState", "categories", "categories")
		this.combinedPricelevels$ = this.store.select("eventServerState", "priceLevels", "combinedPricelevels")
		this.displayCategoriesWithDisplayTickets$ = Observable.combineLatest(
			this.categories$,
			this.combinedPricelevels$,
			this.displayShoppingCart$,
			(categories, combinedPricelevels, displayShoppingCart) =>
				this.mergeCategoryWithCombinedPriceLevelAndDisplayTickets(
					categories, combinedPricelevels, displayShoppingCart && displayShoppingCart.events ? displayShoppingCart.events[0].displayTickets : [])
		)

		// PAYMENT/SHIPMENT
		this.paymentType$ = this.store.select("checkout", "paymentType");
		this.subscriptions.push(this.paymentType$.subscribe(paymentType => this.paymentType = paymentType))
		this.shipmentType$ = this.store.select("checkout", "shipmentType");
		this.subscriptions.push(this.shipmentType$.subscribe(shipmentType => this.shipmentType = shipmentType))
		this.bookingOffice$ = this.merchantApi.getMyBookingOffice();
		this.subscriptions.push(this.bookingOffice$.subscribe(bookingOffice =>
			this.shipmentFees = bookingOffice.shipmentFees[this.shipmentType] ? bookingOffice.shipmentFees[this.shipmentType].money : null))
		this.availablePaymentTypes$ = this.bookingOffice$.map(bookingoffice => bookingoffice.activPaymentTypes);
		this.availableShipmentTypesInfo$ = this.store.select("checkout", "availableShipmentTypesInfo");
		this.subscriptions.push(Observable.combineLatest(this.paymentType$, this.availablePaymentTypes$,
				(selected, available) => {
					let filteredAvailable = this.filterPaymentTypesForBoxOffice(available);
					if (selected == null || filteredAvailable.indexOf(selected) === -1) {
						if (filteredAvailable.indexOf(this.defaultPaymentTypeBoxOffice) === -1) {
							return filteredAvailable[0];
						} else {
							return this.defaultPaymentTypeBoxOffice;
						}
					} else {
						return null;
					}
				}
			).filter(i => i != null).subscribe((paymentType) => this.paymentTypeChange(paymentType))
		);
		this.subscriptions.push(Observable.combineLatest(this.shipmentType$, this.availableShipmentTypesInfo$, this.bookingOffice$,
				(selected, available, bookingOffice) => {
					let filteredAvailable = available ? this.filterShipmentTypesForBoxOffice(available.shipmentTypes) : [];
					let filteredActive = this.filterShipmentTypesForBoxOffice(bookingOffice.activShipmentTypes)
					if (selected == null || filteredAvailable.indexOf(selected) === -1) {
						if (filteredAvailable.indexOf(this.defaultShipmentTypeBoxOffice) === -1) {
							return filteredAvailable[0];
						} else {
							return this.defaultShipmentTypeBoxOffice;
						}
					} else {
						return null;
					}
				}
			).filter(i => i != null).subscribe((shipmentType) => this.shipmentTypeChange(shipmentType))
		);

		// TOTAL
		this.shoppingCartTotal$ = this.store.select("shoppingCart", "displayShoppingCart").map(dsc => {
			let total = 0;
			if (!dsc || !dsc.events) return 0;
			for (let event of dsc.events) {
				for (let dt of event.displayTickets) {
					total += dt.subTotal.money.value
				}
			}
			return total;
		})

		// PRINTING
		this.subscriptions.push(this.store.select("checkout", "documentsToPrint").subscribe(dtp => {
			this.documentsToPrint = dtp
		}));
		this.printerList$ = this.printerApi.getPrinters()
		this.ticketPrinterId = localStorage.getItem("lastUsedPrinter");
		this.store.dispatch(this.checkoutActions.setTicketPrinter(this.ticketPrinterId))

		this.subscriptions.push(this.store.select("ticketAddress").subscribe(data => {
			this.ticketAddresses = data.addresses
			this.amountTicketAdresses = Object.keys(data.addresses).length
		}))

		// ERRORS
		this.store.select("checkout", "errorMessages").subscribe(errorMessages => {
			this.errorMessages = errorMessages
			this.errorMessagesExist = Object.keys(this.errorMessages).map(key => this.errorMessages[key].length > 0).reduce((a, b) => a || b)
		})

		// SEATING-PLAN
		this.seatingPlan$ = this.store.select("eventServerState", "seatingplan");
		this.isSpBoxOffice$ = this.route.data.map(routeData => routeData.boxOfficeSP);
		this.blockSeatElements$ = Observable.combineLatest(this.isSpBoxOffice$, this.seatingPlan$, this.categories$, this.combinedPricelevels$,
			this.displayShoppingCart$, this.selectedTicketQuota$,
			(isSpBoxOffice, seatingPlan, categories, combinedPriceLevels, displayShoppingCart, selectedTicketQuota) => {
				if (!isSpBoxOffice || !seatingPlan || !categories || categories.length === 0 ||
					!combinedPriceLevels || combinedPriceLevels.length === 0 || !selectedTicketQuota) {
					return null;
				}
				const freeSeats = this.getFreeSeats(selectedTicketQuota.ticketQuotaStatus, JSON.parse(seatingPlan.asJson))
				const displayCategories = this.mergeCategoryWithCombinedPriceLevel(categories, combinedPriceLevels);
				const blockElements = freeSeats.filter(blockSeating => blockSeating.categoryUuid)
				.map(blockSeating => {
					return Object.assign(
						{},
						{
							blockSeating,
							displayCategory: JSON.parse(JSON.stringify(displayCategories.find(dspCat => dspCat.category.uuid === blockSeating.categoryUuid)))
						}
					)
				})
				const dspTickets = displayShoppingCart && displayShoppingCart.events ? displayShoppingCart.events[0].displayTickets : [];
				dspTickets.forEach((dspTicket) => {
					const blockElement = blockElements.find((blockEl) => blockEl.blockSeating.block === dspTicket.block);
					if (!blockElement) return;
					if (blockElement.displayCategory.combinedPriceLevel.standard.uuid === dspTicket.priceLevelUuid) {
						blockElement.displayCategory.combinedPriceLevel.standard.displayTicket = dspTicket;
					} else {
						blockElement.displayCategory.combinedPriceLevel.reduced.forEach(rpl => {
							if (rpl.uuid === dspTicket.priceLevelUuid) {
								rpl.displayTicket = dspTicket;
							}
						})
					}
				})
				return blockElements;
			});

		this.visualTickets$ = Observable.combineLatest(this.isSpBoxOffice$,	this.displayShoppingCart$, this.selectedTicketQuota$,
			(isSpBoxOffice, displayShoppingCart, selectedTicketQuota) => {
				if (!isSpBoxOffice || !selectedTicketQuota || !displayShoppingCart) {
					return null;
				}
				const dspTickets = displayShoppingCart && displayShoppingCart.events ? displayShoppingCart.events[0].displayTickets : [];
				return dspTickets.filter(dspTkt => dspTkt.isVisualTicket &&
					dspTkt.ticketQuotaUuid === selectedTicketQuota.uuid);
			});

		this.displayTicketsPerTicketQuota$ = Observable.combineLatest(this.displayShoppingCart$, this.ticketQuotas$, this.selectedTicketQuota$,
			(displayShoppingCart, ticketQuotas, selectedTicketQuota) => {
				if (!ticketQuotas || !selectedTicketQuota || !displayShoppingCart) {
					return [];
				}
				const dspTickets = displayShoppingCart && displayShoppingCart.events ? displayShoppingCart.events[0].displayTickets : [];
				const filteredTicketQuotas = ticketQuotas.filter(tq => tq.uuid !== selectedTicketQuota.uuid)
					.map(ticketQuota => {
						return Object.assign(
							{},
							{
								ticketQuotaName: ticketQuota.name,
								amountOfTickets: dspTickets.filter(dspTicket => dspTicket.ticketQuotaUuid === ticketQuota.uuid)
									.map(dt => dt.numberOfSeats)
									.reduce((acc, cur) => acc + cur, 0),
								subtotal: dspTickets.filter(dspTicket => dspTicket.ticketQuotaUuid === ticketQuota.uuid)
									.map(dt => dt.subTotal.money.value)
									.reduce((acc, cur) => acc + cur, 0),
							}
						)
					})
				return filteredTicketQuotas.filter(item => item.amountOfTickets > 0);
			});
	}

	ngOnDestroy() {
		this.subscriptions.forEach(sub => sub.unsubscribe());
	}

	// TICKETADDRESS
	showTicketAddressForms(): void {
		this.showTicketAddressFormsPopup = true;
	}

	hideTicketAddressForms(): void {
		this.showTicketAddressFormsPopup = false;
	}

	ticketAddressesCompletely(): boolean {
		return this.amountTicketAdresses >= this.numberOfItemsInShoppingCart && this.amountTicketAdresses !== 0
	}


	// SHOPPINGCART
	addTickets(ticketCreationDTOs: TicketCreationDTO[]) {
		this.store.dispatch(this.boxOfficeActions.addTicketsBoxOffice(
			this.shoppingCart._links.tickets.href, ticketCreationDTOs, this.selectedTicketQuota.storageId))
	}

	removeTickets(ticketItemUuidsToBeDeleted: string[]) {
		this.store.dispatch(this.boxOfficeActions.removeTicketsBoxOffice(
			this.shoppingCart._links.tickets.href, ticketItemUuidsToBeDeleted))
	}

	// SHOPPINGCART-SP-BLOCKS
	addBlockTickets(ticketCreationDTOs: TicketCreationDTO[]) {
		this.store.dispatch(this.boxOfficeActions.addVisualBulkTicketsBoxOffice(
			this.shoppingCart._links.visualBulkTickets.href, ticketCreationDTOs, this.selectedTicketQuota.storageId));
	}

	changeSelectedTicketQuota(quota) {
		this.store.dispatch(this.boxOfficeActions.setSelectedTicketQuotaBoxOffice(quota));
	}

	deleteShoppingCart(url?) {
		if (url) {
			this.store.dispatch(this.boxOfficeActions.deleteShoppingCartBoxOffice(url))
		} else {
			if (this.shoppingCart.ticketItems.length > 0) {
				this.store.dispatch(this.boxOfficeActions.deleteShoppingCartBoxOffice(this.shoppingCart._links.self.href))
			}
		}
	}

	mergeCategoryWithCombinedPriceLevelAndDisplayTickets(
		categories: Category[],
		combinedPriceLevels: ui.CombinedPriceLevel[],
		displayTickets: model.DisplayTicket[]): ui.DisplayCategoryWithDisplayTicket[] {
		let results: ui.DisplayCategory[] = []
		for (let category of categories) {
			let foundCombinedPriceLevel = combinedPriceLevels.find(cpl => cpl.categoryID === category.uuid)
			if (foundCombinedPriceLevel) {
				let foundCombinedPriceLevelWithDisplayTicket = this.getCombinedPriceLevelWithDisplayTicket(displayTickets, foundCombinedPriceLevel)
				results.push({
					category: Object.assign({}, category),
					combinedPriceLevel: Object.assign({}, foundCombinedPriceLevelWithDisplayTicket)
				})
			}
		}
		return results;
	}

	getDisplayTicketForCatUuidAndPlUuid(displayTickets, categoryUuid, pricelevelUuid): model.DisplayTicket {
		return displayTickets.filter(displayTicket =>
			displayTicket.categoryUuid === categoryUuid && displayTicket.priceLevelUuid === pricelevelUuid
		)[0];
	}

	getCombinedPriceLevelWithDisplayTicket(
		displayTickets: model.DisplayTicket[], combinedPriceLevel: ui.CombinedPriceLevel): ui.CombinedPriceLevelWithDisplayTicket {
		let combinedPriceLevelWithDisplayTicket: ui.CombinedPriceLevelWithDisplayTicket = Object.assign({}, combinedPriceLevel);
		combinedPriceLevelWithDisplayTicket.standard.displayTicket = this.getDisplayTicketForCatUuidAndPlUuid(
			displayTickets,
			combinedPriceLevelWithDisplayTicket.categoryID,
			combinedPriceLevelWithDisplayTicket.standard.uuid)
		combinedPriceLevelWithDisplayTicket.reduced.map(r => {
			r.displayTicket = this.getDisplayTicketForCatUuidAndPlUuid(displayTickets, combinedPriceLevelWithDisplayTicket.categoryID, r.uuid)
		})
		return combinedPriceLevelWithDisplayTicket
	}

	// PAYMENT/SHIPMENT
	filterShipmentTypesForBoxOffice(shipmentTypes) {
		if (shipmentTypes) {
			return shipmentTypes.filter(st => this.shipmentTypesKeysBoxOffice.indexOf(st) > -1)
		}
	}

	filterPaymentTypesForBoxOffice(paymentTypes) {
		if (paymentTypes) {
			return paymentTypes.filter(pt => this.paymentTypesKeysBoxOffice.indexOf(pt) > -1)
		}
	}

	paymentTypeChange(paymentType: string) {
		this.store.dispatch(this.checkoutActions.setPaymentType(paymentType))
	}

	shipmentTypeChange(shipmentType: string) {
		this.store.dispatch(this.checkoutActions.setShipmentType(shipmentType));
	}

	documentsToPrintChange(docsKey: string, docsVal: boolean) {
		const documentsToPrint = Object.assign({}, this.documentsToPrint, {[docsKey]: docsVal})
		this.store.dispatch(this.checkoutActions.setDocumentsToPrint(documentsToPrint))
	}

	printerChanged(printerId) {
		localStorage.setItem("lastUsedPrinter", printerId);
		this.store.dispatch(this.checkoutActions.setTicketPrinter(printerId))
	}

	scrollToTop() {
		window.scrollTo(0, 0);
	}

	placeOrder() {
		let orderDTO: OrderDTO = {
			customerUUID: null,
			shippingAddress: null,
			billingAddress: null,
			shoppingCartId: this.dscSecureId,
			paymentType: this.paymentType,
			shipmentType: this.shipmentType,
			shipmentFee: this.shipmentFees,
			ticketAddresses: this.amountTicketAdresses > 0 ? this.ticketAddresses : null,
			comment: ""
		}
		this.store.dispatch(this.boxOfficeActions.placeOrderBoxOffice(orderDTO));
		this.scrollToTop();
	}

	// SEATING-PLAN METHODS:
	private getFreeSeats(ticketQuotaStatusColl, seatingPlanElements) {
		if (ticketQuotaStatusColl && seatingPlanElements) {
			const seatingPlanSeats = Object.keys(seatingPlanElements)
				.filter(key => seatingPlanElements[key].type === "FREE_SEATING")
				.map(key => seatingPlanElements[key])

			const freeSeatStatus = this.createFreeSeatStatusMap(ticketQuotaStatusColl)

			return seatingPlanSeats.map(seatingPlanSeat => {
				const seatIdentifier = '{B}' + seatingPlanSeat.block + '␟{R}␟{S}'
				let freeSeat = Object.assign({}, seatingPlanSeat)
				if (freeSeatStatus[seatIdentifier]) {
					freeSeat.totalTickets = freeSeatStatus[seatIdentifier].totalTickets
					freeSeat.availableTickets = freeSeatStatus[seatIdentifier].availableTickets
					freeSeat.fill = freeSeatStatus[seatIdentifier].seatColor
					freeSeat.stroke = freeSeatStatus[seatIdentifier].strokeColor
					freeSeat.categoryUuid = freeSeatStatus[seatIdentifier].categoryUuid
				} else {
					freeSeat.totalTickets = 0
					freeSeat.availableTickets = 0
					freeSeat.fill = '#fff'
					freeSeat.stroke = '#000'
				}
				return freeSeat
			});
		}
		return null;
	}

	private createFreeSeatStatusMap(ticketQuotaStatusColl) {
		let freeSeatStatus = {}
		ticketQuotaStatusColl.forEach(tqStatus => {
			Object.keys(tqStatus.freeSeatingState).forEach(seatIdentifier => {
				let ticketsAvailable = tqStatus.freeSeatingState[seatIdentifier].availableTickets > 0
				freeSeatStatus[seatIdentifier] = {
					totalTickets: tqStatus.freeSeatingState[seatIdentifier].totalTickets,
					availableTickets: tqStatus.freeSeatingState[seatIdentifier].availableTickets,
					seatColor: ticketsAvailable ? tqStatus.category.color : '#fff',
					strokeColor: ticketsAvailable ? 'none' : '#000',
					categoryUuid: tqStatus.category.uuid
				}
			})
		})
		return freeSeatStatus;
	}

	private mergeCategoryWithCombinedPriceLevel(categories: Category[], combinedPriceLevels: ui.CombinedPriceLevel[]): ui.DisplayCategory[] {
		let results: ui.DisplayCategory[] = []
		for (let category of categories) {
			let foundCombinedPriceLevel = combinedPriceLevels.find(cpl => cpl.categoryID === category.uuid)
			results.push({
				category: category,
				combinedPriceLevel: foundCombinedPriceLevel
			})
		}
		return results;
	}

}



// WEBPACK FOOTER //
// ./src/app/components/box-office/box-office.component.ts